import React, { useCallback, useState, useEffect, useMemo } from 'react';
import { Clock, FolderSync } from 'lucide-react';
import { gcAccessToken, gcEnvironment, fetchWrap, getClientOrg } from '../components/common/utilities/fetchWrap';
import { toast } from 'react-toastify';
import { reportConsole } from "../components/common/utilities/logging-utilities";
import config from '../config';

// Type definitions
interface TriggerType {
  id: string;
  name: string;
  selected: boolean;
}

interface CronSchedule {
  id: string;
  name: string;
  expression: string;
}

interface CurrentTrigger {
  type: string;
  schedule: string;
  cronExpression: string;
}

interface KnowledgeBase {
  knowledgeBaseId: string;
}

interface SavedConfig {
  triggerType: string;
  schedule?: string;
  fullSyncInterval?: string;
}

const initialTriggerTypes: TriggerType[] = [
  { id: 'adhoc', name: 'Manual Sync Now', selected: true },
  { id: 'cron', name: 'Scheduled Batch Process (Cron)', selected: false }
];

const initialCronSchedules = [
  { id: 'daily', name: 'Daily at midnight', expression: 'cron(0 0 * * ? *)' },
  { id: 'weekly', name: 'Weekly on Sunday', expression: 'cron(0 0 ? * SUN *)' },
  { id: 'monthly', name: 'Monthly on the 1st', expression: 'cron(0 0 1 * ? *)' },
  { id: '6hours', name: 'Every 6 hours', expression: 'cron(0 */6 * * ? *)' },
  { id: '12hours', name: 'Every 12 hours', expression: 'cron(0 */12 * * ? *)' },
  { id: 'custom', name: 'Custom...', expression: '' }
];

const initialCurrentTrigger: CurrentTrigger = {
  type: 'adhoc',
  schedule: 'daily',
  cronExpression: 'cron(0 0 * * ? *)'
};

const API_BASE_URL = config.endpoints.endpoint || '';

const TriggerConfig: React.FC = () => {
  // State management
  const [triggerTypes, setTriggerTypes] = useState<TriggerType[]>(initialTriggerTypes);
  const [cronSchedules] = useState<CronSchedule[]>(initialCronSchedules);
  const [currentTrigger, setCurrentTrigger] = useState<CurrentTrigger>(initialCurrentTrigger);
  const [knowledgeBases, setKnowledgeBases] = useState<KnowledgeBase[]>([]);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  // Custom hooks
  const useKnowledgeBaseApi = () => {
    const orgId = getClientOrg();

    const getRequestHeaders = useCallback(() => ({
      'Content-Type': 'application/json',
      env: gcEnvironment(),
      token: gcAccessToken(),
      tokensource: 'purecloud'
    }), []);

    const fetchKnowledgeBases = useCallback(async (): Promise<any[]> => {
      try {
        const response = await fetchWrap({
          url: `${API_BASE_URL}/getSPConfiguration`,
          options: {
            method: 'POST',
            body: JSON.stringify({ tenantId: orgId, system: 'target' })
          },
          token: gcAccessToken(),
          auth: true,
          cookies: false,
          headerOptions: getRequestHeaders(),
          noContentType: false,
          contentType: 'application/json',
          noResponseData: false
        });

        return response.data.results || [];
      } catch (err: any) {
        reportConsole('Failed to fetch knowledge bases', 'error', { err });
        throw new Error(err.message || 'Failed to fetch knowledge bases');
      }
    }, [orgId, getRequestHeaders]);

    const setScheduledScan = useCallback(async (knowledgeBase: any, cronExpression: string, action: string): Promise<any> => {
      try {
        const syncConfig = { schedule: cronExpression };
        const response = await fetchWrap({
          url: `${API_BASE_URL}/runScheduleScan`,
          options: {
            method: 'POST',
            body: JSON.stringify({ tenantId: orgId, knowledgeBase, action, syncConfig })
          },
          token: gcAccessToken(),
          auth: true,
          cookies: false,
          headerOptions: getRequestHeaders(),
          noContentType: false,
          contentType: 'application/json',
          noResponseData: false
        });

        return response.data.results || [];
      } catch (err: any) {
        reportConsole('Failed to set scheduled scan', 'error', { err });
        throw new Error(err.message || 'Failed to set scheduled scan');
      }
    }, [orgId, getRequestHeaders]);

    const removeScheduledScan = useCallback(async (knowledgeBase: any, action: string): Promise<any> => {
      try {
        const response = await fetchWrap({
          url: `${API_BASE_URL}/runScheduleScan`,
          options: {
            method: 'POST',
            body: JSON.stringify({ tenantId: orgId, knowledgeBase, action })
          },
          token: gcAccessToken(),
          auth: true,
          cookies: false,
          headerOptions: getRequestHeaders(),
          noContentType: false,
          contentType: 'application/json',
          noResponseData: false
        });

        return response.data.results || [];
      } catch (err: any) {
        reportConsole('Failed to remove scheduled scan', 'error', { err });
        throw new Error(err.message || 'Failed to remove scheduled scan');
      }
    }, [orgId, getRequestHeaders]);

    const setAdhocScan = useCallback(async (knowledgeBase: any): Promise<any> => {
      try {
        const response = await fetchWrap({
          url: `${API_BASE_URL}/runAdHocScan`,
          options: {
            method: 'POST',
            body: JSON.stringify({ tenantId: orgId, knowledgeBase })
          },
          token: gcAccessToken(),
          auth: true,
          cookies: false,
          headerOptions: getRequestHeaders(),
          noContentType: false,
          contentType: 'application/json',
          noResponseData: false
        });

        return response.data.results || [];
      } catch (err: any) {
        reportConsole('Failed to set ad-hoc scan', 'error', { err });
        throw new Error(err.message || 'Failed to set ad-hoc scan');
      }
    }, [orgId, getRequestHeaders]);

    const fetchSavedConfig = useCallback(async (): Promise<SavedConfig> => {
      try {
        const response = await fetchWrap({
          url: `${API_BASE_URL}/getSPConfiguration`,
          options: {
            method: 'POST',
            body: JSON.stringify({ tenantId: orgId, system: 'sync' })
          },
          token: gcAccessToken(),
          auth: true,
          cookies: false,
          headerOptions: getRequestHeaders(),
          noContentType: false,
          contentType: 'application/json',
          noResponseData: false
        });

        return response.data.results || {};
      } catch (err: any) {
        reportConsole('Failed to fetch trigger configuration', 'error', { err });
        throw new Error(err.message || 'Failed to fetch trigger configuration');
      }
    }, [orgId, getRequestHeaders]);

    const saveTriggerConfig = useCallback(async (config: SavedConfig): Promise<any> => {
      try {
        const response = await fetchWrap({
          url: `${API_BASE_URL}/setTriggerConfiguration`,
          options: {
            method: 'POST',
            body: JSON.stringify({ tenantId: orgId, nestedData: config })
          },
          token: gcAccessToken(),
          auth: true,
          cookies: false,
          headerOptions: getRequestHeaders(),
          noContentType: false,
          contentType: 'application/json',
          noResponseData: false
        });

        return response.data.results || [];
      } catch (err: any) {
        reportConsole('Failed to save trigger configuration', 'error', { err });
        throw new Error(err.message || 'Failed to save trigger configuration');
      }
    }, [orgId, getRequestHeaders]);

    return { fetchKnowledgeBases, setScheduledScan, removeScheduledScan, setAdhocScan, fetchSavedConfig, saveTriggerConfig };
  };

  const { fetchKnowledgeBases, setScheduledScan, removeScheduledScan, setAdhocScan, fetchSavedConfig, saveTriggerConfig } = useKnowledgeBaseApi();

  // Validate cron expression
  const isValidCronExpression = useCallback((expression: string): boolean => {
    let cronContent = expression;
    if (cronContent.startsWith('cron(') && cronContent.endsWith(')')) {
      cronContent = cronContent.slice(5, -1);
    }
    const fields = cronContent.split(' ');
    if (fields.length !== 6) {
      return false;
    }
     // eslint-disable-next-line no-useless-escape
    const minuteRegex = /^(\*|[0-5]?[0-9]|\,|\/|\-)+$/;
     // eslint-disable-next-line no-useless-escape
    const hourRegex = /^(\*|[01]?[0-9]|2[0-3]|\,|\/|\-)+$/;
     // eslint-disable-next-line no-useless-escape
    const dayOfMonthRegex = /^(\*|\?|[1-2]?[0-9]|3[01]|\,|\/|\-|L|W)+$/;
     // eslint-disable-next-line no-useless-escape
    const monthRegex = /^(\*|[1-9]|1[0-2]|JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|OCT|NOV|DEC|\,|\/|\-)+$/i;
     // eslint-disable-next-line no-useless-escape
    const dayOfWeekRegex = /^(\*|\?|[0-7]|SUN|MON|TUE|WED|THU|FRI|SAT|\,|\/|\-|L|#)+$/i;
     // eslint-disable-next-line no-useless-escape
    const yearRegex = /^(\*|19[7-9][0-9]|20[0-9][0-9]|21[0-9][0-9]|\,|\/|\-)+$/;

    const regexes = [minuteRegex, hourRegex, dayOfMonthRegex, monthRegex, dayOfWeekRegex, yearRegex];

    if (fields[2] !== '?' && fields[4] !== '?') {
      return false;
    }

    for (let i = 0; i < 6; i++) {
      if (!regexes[i].test(fields[i])) {
        return false;
      }
    }

    return true;
  }, []);

  // Handlers
  const handleTriggerTypeChange = useCallback((typeId: string) => {
    setTriggerTypes(prev =>
      prev.map(type => ({
        ...type,
        selected: type.id === typeId
      }))
    );
    setCurrentTrigger(prev => ({
      ...prev,
      type: typeId
    }));
    setErrorMessage('');
  }, []);

  const handleCronScheduleChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const scheduleId = e.target.value;
    const selectedSchedule = cronSchedules.find(schedule => schedule.id === scheduleId);

    if (selectedSchedule) {
      setCurrentTrigger(prev => ({
        ...prev,
        schedule: scheduleId,
        cronExpression: selectedSchedule.expression || prev.cronExpression
      }));
      setErrorMessage('');
    }
  }, [cronSchedules]);

  const handleCronExpressionChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCurrentTrigger(prev => ({
      ...prev,
      cronExpression: value,
      schedule: 'custom'
    }));

    if (value && !isValidCronExpression(value)) {
      setErrorMessage('Invalid cron expression format');
    } else {
      setErrorMessage('');
    }
  }, [isValidCronExpression]);

  const fetchKnowledgeBasesForTenant = useCallback(async () => {
    try {
      const [knowledgeBasesData, savedConfig] = await Promise.all([
        fetchKnowledgeBases(),
        fetchSavedConfig()
      ]);

      setKnowledgeBases(knowledgeBasesData);

      if ('triggerType' in savedConfig) {
        setTriggerTypes(prev =>
          prev.map(type => ({
            ...type,
            selected: type.id === savedConfig.triggerType
          }))
        );

        if (savedConfig.triggerType === 'cron' && savedConfig.schedule) {
          const matchingSchedule = cronSchedules.find(
            schedule => schedule.expression === savedConfig.schedule
          );

          setCurrentTrigger({
            type: savedConfig.triggerType,
            schedule: matchingSchedule ? matchingSchedule.id : 'custom',
            cronExpression: savedConfig.schedule
          });
        }
      }
    } catch (error: any) {
      setErrorMessage(error.message || 'Failed to fetch configurations');
      reportConsole(error, "error");
    }
  }, [fetchKnowledgeBases, fetchSavedConfig, cronSchedules]);

  const handleSaveConfiguration = useCallback(async () => {
    if (currentTrigger.type === 'cron') {
      if (!isValidCronExpression(currentTrigger.cronExpression)) {
        toast.error('Please enter a valid cron expression');
        return;
      }
      setIsLoading(true);
      try {
        const cronExp = cronSchedules.find(schedule => schedule.id === currentTrigger.schedule)?.expression || currentTrigger.cronExpression;

        await saveTriggerConfig({
          triggerType: currentTrigger.type,
          fullSyncInterval: currentTrigger.schedule,
          schedule: cronExp
        });

        await Promise.all(knowledgeBases.map(async (kb) => {
          await setScheduledScan(kb, cronExp, 'create');
        }));

        toast.success("Trigger configuration saved for all knowledge bases successfully");
        setErrorMessage('');
      } catch (error: any) {
        setErrorMessage(error.message || 'Failed to save configuration');
        reportConsole(error, "error");
        toast.error("Failed to save trigger configuration");
      } finally {
        setIsLoading(false);
        setIsModalOpen(false);
      }
    }
  }, [currentTrigger, cronSchedules, knowledgeBases, setScheduledScan, isValidCronExpression, saveTriggerConfig]);

  const handleAdhocScanRun = useCallback(async () => {
    if (currentTrigger.type === 'adhoc') {
      if (knowledgeBases.length === 0) {
        toast.error('No Active Knowledge Base Found!');
        return;
      }
      setIsLoading(true);
      try {
        await Promise.all(knowledgeBases.map(kb => setAdhocScan(kb)));
        await saveTriggerConfig({
          triggerType: currentTrigger.type,
          fullSyncInterval: 'On demand',
          schedule: 'NA'
        });
        await Promise.all(knowledgeBases.map(async (kb) => {
          await removeScheduledScan(kb, 'delete');
        }));
        toast.success("Adhoc Trigger configuration saved for all knowledge bases successfully");
        setErrorMessage('');
      } catch (error: any) {
        setErrorMessage(error.message || 'Failed to trigger manual sync');
        toast.error('Failed to trigger manual sync');
      } finally {
        setIsLoading(false);
        setIsModalOpen(false);
      }
    }
  }, [currentTrigger, knowledgeBases, setAdhocScan, removeScheduledScan, saveTriggerConfig]);

  // Memoized selected trigger type
  const selectedTriggerType = useMemo(() =>
    triggerTypes.find(type => type.selected)?.id || 'cron',
    [triggerTypes]
  );

  // Initial fetch
  useEffect(() => {
    fetchKnowledgeBasesForTenant();
  }, [fetchKnowledgeBasesForTenant]);

  // Modal handlers
  const openModal = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  return (
    <div className="pt-16 ml-64">
      <div className="p-6">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-lg font-medium mb-6">Trigger Type</h2>

          <div className="space-y-4" role="radiogroup">
            {triggerTypes.map((type) => (
              <div key={type.id} className="flex items-center">
                <input
                  id={`trigger-type-${type.id}`}
                  name="triggerType"
                  type="radio"
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                  checked={type.selected}
                  onChange={() => handleTriggerTypeChange(type.id)}
                  aria-label={type.name}
                />
                <label htmlFor={`trigger-type-${type.id}`} className="ml-3 flex items-center text-sm font-medium text-gray-700">
                  {type.id === 'cron' && <Clock size={16} className="mr-2" />}
                  {type.id === 'adhoc' && <FolderSync size={16} className="mr-2" />}
                  {type.name}
                </label>
              </div>
            ))}
          </div>

          {selectedTriggerType === 'cron' && (
            <div className="mt-6">
              <h3 className="text-lg font-medium mb-4">Cron Schedule Configuration</h3>

              <div className="mb-4">
                <label htmlFor="cron-expression" className="block text-sm font-medium text-gray-700 mb-1">
                  Cron Expression
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Clock size={16} className="text-gray-400" />
                  </div>
                  <input
                    id="cron-expression"
                    type="text"
                    value={currentTrigger.cronExpression}
                    onChange={handleCronExpressionChange}
                    className={`pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm ${errorMessage ? 'border-red-500' : ''}`}
                    placeholder="0 0 * * * *"
                    aria-invalid={!!errorMessage}
                    aria-describedby={errorMessage ? 'cron-error' : undefined}
                  />
                </div>
                <p className="mt-1 text-sm text-gray-500">
                  {currentTrigger.schedule === 'daily' && 'Runs every day at midnight (server time)'}
                  {currentTrigger.schedule === 'weekly' && 'Runs every Sunday at midnight (server time)'}
                  {currentTrigger.schedule === 'monthly' && 'Runs on the 1st of each month at midnight (server time)'}
                  {currentTrigger.schedule === '6hours' && 'Runs every 6 hours (00:00, 06:00, 12:00, 18:00)'}
                  {currentTrigger.schedule === '12hours' && 'Runs every 12 hours (00:00, 12:00)'}
                  {currentTrigger.schedule === 'custom' && 'Custom cron schedule'}
                </p>
              </div>

              <div className="mb-4">
                <label htmlFor="preset-schedules" className="block text-sm font-medium text-gray-700 mb-1">
                  Preset Schedules
                </label>
                <select
                  id="preset-schedules"
                  value={currentTrigger.schedule}
                  onChange={handleCronScheduleChange}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                >
                  {cronSchedules.map((schedule) => (
                    <option key={schedule.id} value={schedule.id}>
                      {schedule.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {selectedTriggerType === 'adhoc' && (
            <div className="mt-6">
              <h3 className="text-lg font-medium mb-4">Ad-hoc Update Configuration</h3>
              <p className="text-gray-600 mb-4">
                Ad-hoc updates allow you to manually trigger the synchronization process whenever needed.
                No automatic scheduling will be set up.
              </p>
              <div className="mt-6 flex justify-end">
                <button
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-300"
                  onClick={openModal}
                  disabled={isLoading}
                >
                  <FolderSync size={16} className="mr-2" />
                  {isLoading ? 'Saving...' : 'Save Configuration'}
                </button>
              </div>
            </div>
          )}

          {(selectedTriggerType === 'cron' || selectedTriggerType === 'webhook') && (
            <div className="mt-6 flex justify-end">
              <button
                className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-300"
                onClick={openModal}
                disabled={isLoading || (selectedTriggerType === 'cron' && !!errorMessage)}
              >
                <FolderSync size={16} className="mr-2" />
                {isLoading ? 'Saving...' : 'Save Configuration'}
              </button>
            </div>
          )}

          {/* Modal */}
          {isModalOpen && (
            <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center z-50">
              <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Confirm Configuration Save</h3>
                <p className="text-sm text-gray-500 mb-6">
                  Are you sure you want to save the trigger configuration for all knowledge bases?
                </p>
                <div className="flex justify-end space-x-3">
                  <button
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    onClick={closeModal}
                    disabled={isLoading}
                  >
                    Cancel
                  </button>
                  <button
                    className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-300"
                    onClick={selectedTriggerType === 'adhoc' ? handleAdhocScanRun : handleSaveConfiguration}
                    disabled={isLoading}
                  >
                    {isLoading ? 'Saving...' : 'Confirm'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TriggerConfig;